import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { FC, Suspense, useEffect, useRef } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { DropdownItemModel } from '../../../../pages/SingleUnit';
import { ReportData, ReportGroupBy, ReportOrderBy, ReportSubtype } from '../../../../models/Report';
import ReportHubModalGroupByFilter from './ReportHubModalGroupByFilter';
import { PreloadedQuery } from 'react-relay';
import { ReportHubGetStandardsBySitesQuery } from '../../../../graphQLOperations/__generated__/ReportHubGetStandardsBySitesQuery.graphql';
import { AllDropdowns } from './ReportHubModal';
import { useAppSelector } from '../../../../redux/hooks';
import { useSideNavActive } from '../../../LayoutWrapper/SideNavLayoutContext';
import { selectedReportsFilter } from '../../../../redux/reducers/filterReportsSlice';

interface Props {
  values: ReportData;
  setValues: (values: ReportData | ((prev: ReportData) => ReportData)) => void;
  allDropdowns: AllDropdowns;
  standards: DropdownItemModel[];
  setStandards: (standards: DropdownItemModel[]) => void;
  selectedReportSubtype: ReportSubtype | null;
  queryStandardsReference: PreloadedQuery<ReportHubGetStandardsBySitesQuery, Record<string, unknown>>;
  isAdmin: boolean;
  isReduxFiltersUsed?: boolean;
}

const ReportHubModalFilters: FC<Props> = ({
  values,
  setValues,
  allDropdowns,
  standards,
  setStandards,
  selectedReportSubtype,
  queryStandardsReference,
  isAdmin,
  isReduxFiltersUsed = false,
}) => {
  const reportsFilterRedux = useAppSelector(selectedReportsFilter);
  const sideNavActive = useSideNavActive();
  const seededFromGlobalFilter = useRef(false);
  const limitCommas = (str: string, maxCommas = 3) => {
    const [grades, site] = str.split(' - ');
    const parts = grades.split(',');
    if (parts.length > maxCommas) {
      return parts.slice(0, maxCommas).join(',') + ' ... - ' + site;
    }
    return str;
  };

  const classesTemplate = (option: { label: string }) => {
    const [classDisplay, gradesAndSite] = option.label.split(' ~ ');
    if (isAdmin && gradesAndSite) {
      return (
        <div className="flex flex-column">
          <span>{classDisplay}</span>
          <span>{limitCommas(gradesAndSite)}</span>
        </div>
      );
    }
    return <span>{classDisplay}</span>;
  };

  useEffect(() => {
    if (!sideNavActive || isReduxFiltersUsed || seededFromGlobalFilter.current) return;
    if (!reportsFilterRedux?.classes?.length) return;

    seededFromGlobalFilter.current = true;
    setValues(prev => ({
      ...prev,
      roadmap: reportsFilterRedux.roadmaps?.id || prev.roadmap,
      sites: reportsFilterRedux.sites?.id || prev.sites,
      classes: reportsFilterRedux.classes.map(cl => cl.id),
    }));
  }, [sideNavActive, isReduxFiltersUsed, reportsFilterRedux, setValues]);

  useEffect(() => {
    if (isReduxFiltersUsed && reportsFilterRedux) {
      setValues(prev => ({
        ...prev,
        roadmap: reportsFilterRedux.roadmaps.id,
        classes: reportsFilterRedux.classes.map(cl => cl.id),
        sites: reportsFilterRedux.sites.id,
        standards: standards.length === 1 ? standards[0].value : '',
      }));
    }
  }, [isReduxFiltersUsed]);

  return (
    <>
      <div className="flex justify-content-between items-center pt-2">
        <h1 className="md:text-base text-lg">Report Settings</h1>
        <Button
          label="Reset All"
          onClick={() =>
            setValues({
              roadmap: '',
              classes: [],
              sites: allDropdowns.sites.length === 1 ? allDropdowns.sites[0].value : '',
              standards: standards.length === 1 ? standards[0].value : '',
              groupedBy: ReportGroupBy.UNIT,
              orderBy: ReportOrderBy.ROWS,
            })
          }
          outlined
          className="border-none"
        />
      </div>
      {allDropdowns.sites && allDropdowns.sites.length > 1 && (
        <div className="field col-12 mb-1">
          <label htmlFor="classes">Site*</label>
          <Dropdown
            value={values.sites}
            optionLabel="label"
            id="sites"
            className="w-full"
            onChange={e => setValues({ ...values, sites: e.target.value, classes: [] })}
            options={allDropdowns.sites}
          />
        </div>
      )}
      <div className="field col-12 mb-1">
        <label htmlFor="classes">Roadmap*</label>
        <Dropdown
          value={values.roadmap}
          optionLabel="label"
          id="roadmap"
          className="w-full"
          filter
          onChange={e => setValues({ ...values, roadmap: e.target.value })}
          options={allDropdowns.roadmaps}
        />
      </div>
      <div className="field col-12 mb-1">
        <label htmlFor="classes">Class(es)*</label>
        <MultiSelect
          value={values.classes}
          itemTemplate={classesTemplate}
          optionLabel="label"
          display="chip"
          id="classes"
          className="w-full"
          filter
          onChange={e => setValues({ ...values, classes: e.target.value })}
          options={allDropdowns.classes}
        />
      </div>
      {selectedReportSubtype === ReportSubtype.PROGRESS_DETAILED_SKILL_REPORT && queryStandardsReference && (
        <Suspense>
          <ReportHubModalGroupByFilter
            values={values}
            setValues={setValues}
            standards={standards}
            setStandards={setStandards}
            queryStandardsReference={queryStandardsReference}
          />
        </Suspense>
      )}
    </>
  );
};

export default ReportHubModalFilters;
